import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import {
  confirmSignIn,
  fetchUserAttributes,
  getCurrentUser,
  signIn,
} from 'aws-amplify/auth';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import TextFieldAuth from '../../../components/custom/TextFieldAuth';
import { AppUser, useAuth } from '../../../utils/context/useAuthContext';
import { UserRole } from '../../../utils/types/types';

interface LogInFormValues {
  email: string;
  phone: string;
  code: string;
}

const OTP: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const auth = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [activeButton, setActiveButton] = useState<'phone' | 'email'>('email');
  const [showCodeInput, setShowCodeInput] = useState(false);

  const {
    handleSubmit,
    control,
    watch,
    setError,
    formState: { errors },
    clearErrors,
  } = useForm<LogInFormValues>({
    defaultValues: {
      email: '',
      phone: '',
      code: '',
    },
  });

  const emailValue = watch('email');

  const parseVerifiedAttribute = (
    attr: string | undefined,
  ): boolean | undefined => {
    if (attr === undefined) return undefined;
    return attr.toLowerCase() === 'true';
  };

  const onSubmit = async (data: LogInFormValues) => {
    setIsLoading(true); // Use setIsLoading
    clearErrors();

    try {
      if (!showCodeInput) {
        let username: string | undefined;
        let response: any;

        if (activeButton === 'email') {
          username = data.email;
          if (!username) {
            alert('Please enter your Email');
            setIsLoading(false); // Use setIsLoading
            return;
          }
          response = await signIn({
            username,
            options: {
              authFlowType: 'CUSTOM_WITHOUT_SRP',
              clientMetadata: {
                preferred_delivery: 'EMAIL',
                email: data.email || '',
                phone_number: data.phone || '',
              },
            },
          });
          if (
            response.nextStep?.signInStep ===
            'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE'
          ) {
            setShowCodeInput(true);
          } else {
            throw new Error(response.nextStep?.signInStep || 'OtpSendFailed');
          }
        } else if (activeButton === 'phone') {
          username = data.phone;
          if (!username) {
            alert('Please enter your Phone number');
            setIsLoading(false); // Use setIsLoading
            return;
          }
          if (!/^\+[1-9]\d{1,14}$/.test(username)) {
            alert(
              'Invalid phone number format. Please use E.164 format (e.g., +15551234567).',
            );
            setIsLoading(false); // Use setIsLoading
            return;
          }
          response = await signIn({
            username,
            options: {
              authFlowType: 'CUSTOM_WITHOUT_SRP',
              clientMetadata: { preferred_delivery: 'SMS' },
            },
          });
          if (
            response.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' ||
            response.nextStep?.signInStep ===
              'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE'
          ) {
            setShowCodeInput(true);
          } else {
            throw new Error(response.nextStep?.signInStep || 'OtpSendFailed');
          }
        } else {
          alert('Invalid selection. Please choose Email or Phone.');
          setIsLoading(false); // Use setIsLoading
          return;
        }
      } else {
        if (!data.code) {
          alert('Please enter the code you received');
          setIsLoading(false); // Use setIsLoading
          return;
        }

        const confirmResponse = await confirmSignIn({
          challengeResponse: data.code,
        });

        if (confirmResponse.isSignedIn) {
          const cognitoUser = await getCurrentUser();
          const attributes = await fetchUserAttributes();
          const userData: AppUser = {
            userId: cognitoUser.userId,
            email: attributes.email,
            email_verified: parseVerifiedAttribute(attributes.email_verified),
            phone_number: attributes.phone_number,
            phone_number_verified: parseVerifiedAttribute(
              attributes.phone_number_verified,
            ),
            role: attributes['custom:role'] as UserRole | undefined,
          };

          auth.login(userData);

          if (userData.role === UserRole.ADMIN) {
            navigate('/clients', { replace: true });
          } else if (userData.role === UserRole.AGENT) {
            navigate('/agent-dashboard', { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        } else {
          throw new Error(
            confirmResponse.nextStep?.signInStep || 'ConfirmationFailed',
          );
        }
      }
    } catch (error: any) {
      console.error('Error during OTP process:', error);
      const errorCode = error.name || error.message || 'default';
      let friendlyMessage = `An error occurred: ${error.message}`;

      if (
        errorCode === 'CodeMismatchException' ||
        errorCode.includes('Invalid code')
      ) {
        friendlyMessage =
          'The code you entered is incorrect. Please try again.';
        setError('code', { type: 'manual', message: friendlyMessage });
      } else if (errorCode === 'ExpiredCodeException') {
        friendlyMessage = 'The code has expired. Please request a new one.';
        setShowCodeInput(false);
      } else if (errorCode === 'LimitExceededException') {
        friendlyMessage = 'Attempt limit exceeded. Please try again later.';
      } else if (errorCode === 'UserNotFoundException') {
        friendlyMessage =
          'User not found. Please check your email or phone number.';
        setShowCodeInput(false);
      } else if (errorCode.includes('Invalid phone number format')) {
        friendlyMessage =
          'Invalid phone number format. Please use E.164 format (e.g., +15551234567).';
      } else if (errorCode.includes('Username cannot be empty')) {
        friendlyMessage = 'Please provide a valid Email or Phone number.';
      }

      setError('code', { type: 'manual', message: friendlyMessage });
    } finally {
      setIsLoading(false); // Use setIsLoading
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: '25px',
        background: 'transparent',
        my: '20px',
        gap: '20px',
        borderRadius: '20px',
        width: '513px',
      }}
      autoComplete="on"
    >
      <Box
        component="img"
        src="/logo.svg"
        sx={{
          alignSelf: 'flex-end',
          maxWidth: '300px',
          height: 'auto',
        }}
      />

      <Stack justifyContent={'flex-end'} sx={{ width: '100%' }}>
        <Typography
          fontWeight={'800'}
          fontSize={'22px'}
          fontFamily={'Heebo'}
          textAlign={'right'}
          color="#ffffff"
        >
          {showCodeInput
            ? 'שלחנו לך סיסמה חד פעמית למייל'
            : 'שלחו לי סיסמה חד פעמית למייל'}
        </Typography>
      </Stack>

      <Stack spacing={2} width={'100%'}>
        {activeButton === 'email' ? (
          showCodeInput ? (
            <Stack spacing={0.5}>
              <Typography
                sx={{
                  color: theme.palette.white.main,
                  textAlign: 'right',

                  fontFamily: 'AlmoniDL',
                  fontWeight: 700,
                  fontSize: '16px',
                }}
              >
                {emailValue}
              </Typography>
            </Stack>
          ) : (
            <Controller
              name="email"
              control={control}
              rules={{
                required: 'חובה למלא את השדה',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'כתובת אימייל לא תקינה',
                },
              }}
              render={({ field }) => (
                <TextFieldAuth
                  {...field}
                  id="email"
                  label="כתובת מייל"
                  fullWidth
                  autoComplete="username"
                  error={!!errors.email}
                  helperText={errors.email ? errors.email.message : ''}
                />
              )}
            />
          )
        ) : (
          <Controller
            name="phone"
            control={control}
            rules={{
              required: 'חובה למלא את השדה',
            }}
            render={({ field }) => (
              <TextFieldAuth
                {...field}
                id="phone"
                label="מספר טלפון"
                fullWidth
                error={!!errors.phone}
                helperText={errors.phone ? errors.phone.message : ''}
                disabled={showCodeInput}
              />
            )}
          />
        )}

        {showCodeInput && (
          <Controller
            name="code"
            control={control}
            rules={{
              required: 'חובה למלא את הקוד',
            }}
            render={({ field }) => (
              <TextFieldAuth
                {...field}
                id="code"
                label="הזן את הקוד שקיבלת"
                fullWidth
                error={!!errors.code}
                helperText={errors.code ? errors.code.message : ''}
              />
            )}
          />
        )}
      </Stack>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Button
            sx={{
              width: '52px',
              height: '52px',
              minWidth: '52px',
              minHeight: '52px',
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
              backgroundColor: '#FFFFFF33',
              padding: 0,
              borderRadius: '8px',
            }}
            type="submit"
            disabled={isLoading}
          >
            <img
              alt="auth"
              src="/icons/auth.svg"
              width={'22.3px'}
              height={'25.36px'}
            />
          </Button>
          <Typography
            fontSize="16px"
            fontWeight="700"
            fontFamily={'AlmoniDL'}
            color={theme.palette.white.main}
          >
            כניסה
          </Typography>
        </Box>

        <Typography
          fontSize="16px"
          fontWeight="700"
          fontFamily={'AlmoniDL'}
          sx={{ cursor: 'pointer' }}
          color={theme.palette.blue.main}
          onClick={() => {
            if (showCodeInput) {
              setShowCodeInput(false);
            } else {
              navigate(-1);
            }
          }}
        >
          {showCodeInput ? ' שנה מייל' : 'חזרה'}
        </Typography>
      </Box>
    </Box>
  );
};

export default OTP;
